import { createContext, CSSProperties, useContext, useEffect, useRef, useState } from "react";
import { mdiFolderUpload, mdiImageArea, mdiUpload, mdiWeatherNight, mdiWeatherSunny } from "@mdi/js";
import Icon from "@mdi/react";

import { useLang } from "../services/lang";
import { Switch } from ".";
import { useLocalState } from "../services/common";
import { Theme } from "../../types";
import { resizeImage } from "easy-image-resizer";

type Mode = "dark" | "light";

type ThemeContextType = {
    mode: Mode;
    setMode: (mode: Mode) => void;
};


const ThemeContext = createContext<ThemeContextType>({
    mode: "dark",
    setMode: () => { },
});


export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [modeUser, setModeUser] = useLocalState<null | Mode>("mode", null);
    const [modeSystem, setModeSystem] = useState<null | Mode>(null);

    useEffect(() => {
        const handleColorSchemeChange = (event: MediaQueryListEvent) => {
            setModeSystem(event.matches ? "dark" : "light");
        };

        const mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
        mediaQueryList.addEventListener('change', handleColorSchemeChange);

        setModeSystem(mediaQueryList.matches ? "dark" : "light");

        return () => {
            mediaQueryList.removeEventListener('change', handleColorSchemeChange);
        };
    }, []);

    const mode = modeUser ?? modeSystem ?? "dark";

    return <ThemeContext.Provider value={{ mode, setMode: setModeUser }}>
        <div className={`${mode}-mode`}>{children}</div>
    </ThemeContext.Provider>
}

export function ThemeSwitch() {
    const { mode, setMode } = useContext(ThemeContext);
    return <div className="theme-mode-switch">
        <Icon path={mdiWeatherSunny} size={1} />
        <Switch value={mode === "dark"} onChange={dark => setMode(dark ? "dark" : "light")} />
        <Icon path={mdiWeatherNight} size={1} />
    </div>;
}

export function ThemeDiv({ theme, className, preview = false, children, onClick }: { theme?: Theme, className?: string, preview?: boolean, children: React.ReactNode, onClick?: () => void }) {
    return <div className={`${className} ${getThemeClassName(theme)}`} onClick={onClick}>
        {typeof theme === "object" && <>
            <div
                className="theme-background"
                style={{
                    opacity: theme.backgroundOpacity,
                    backgroundImage: `url(${preview ? theme.backgroundPreview.url : theme.background.url})`
                }}
            />
            <div className="theme-content">
                {children}
            </div>
        </>}
        {typeof theme !== "object" && children}
    </div>;
}


export function ThemeSelect({ value, hidePreview = false, onChange }: { value: Theme, hidePreview?: boolean, onChange: (value: Theme) => void }) {
    const { dict } = useLang();

    const inputFileRef = useRef<HTMLInputElement>(null);

    const options: { value: Theme, label: string }[] = [
        { value: "", label: dict.themeSystem },
        { value: "light", label: dict.themeLight },
        { value: "dark", label: dict.themeDark },
        { value: "parchment", label: dict.themeParchment },
    ];

    function handleCustom() {
        inputFileRef.current?.click();
    }

    async function handleImage(file?: File) {
        if (!file) return;

        const background = await resizeImage(file, { maxWidth: 1024, maxHeight: 1024, type: "webp", quality: 0.8 });
        const backgroundPreview = await resizeImage(file, { maxWidth: 250, maxHeight: 250, type: "webp", quality: 0.9 });

        const luminance = await getLuminanceFromImage(backgroundPreview);

        const mode = luminance < 128 ? "dark" : "light";

        const luminanceStrong = Math.abs(luminance - 128) / 128;
        const backgroundOpacity = luminanceStrong * 0.5 + 0.3;

        onChange({
            type: "custom",
            mode,
            backgroundOpacity,
            background: { type: "EASY_DB_FILE", url: background },
            backgroundPreview: { type: "EASY_DB_FILE", url: backgroundPreview },
        });
    }

    return <>
        <input
            className="hidden-input" ref={inputFileRef} type="file" accept=".png, .jpg, .jpeg, .webp"
            onChange={e => handleImage(e.target.files?.[0])}
        />
        <div className="theme-switcher">
            {options.map(o => <div
                key={o.value as string}
                className={`theme-option theme-${o.value} ${o.value === value ? "active" : ""}`}
                onClick={() => onChange(o.value)}
            >
                {o.label}
            </div>)}
            <div
                title={dict.themeCustom.description}
                className={`theme-option theme-custom ${typeof value === "object" ? "active" : ""}`}
                onClick={handleCustom}
            >
                <div className="background">
                    <Icon path={mdiFolderUpload} size={2} />
                </div>
                <span>{dict.themeCustom.title}</span>
            </div>
        </div>
        {!hidePreview && typeof value === "object" && <div className="theme-preview">
            <ThemeDiv theme={value} className={`block mobile`}>
                <h3>{dict.themeCustom.previewMobile}</h3>
                <p>{dict.themeCustom.previewTextMobile}</p>
            </ThemeDiv>
            <ThemeDiv theme={value} className={`block desktop`}>
                <h3>{dict.themeCustom.previewDesktop}</h3>
                <p>{dict.themeCustom.previewTextDesktop}</p>
            </ThemeDiv>
        </div>}
    </>;
}

// TODO: make it generic
function getThemeClassName(theme?: Theme) {
    if (!theme) return "";
    if (typeof theme !== "string") return `${theme.mode}-mode theme-custom`;

    if (["light", "parchment"].includes(theme)) return `light-mode theme-${theme}`;
    if (["dark"].includes(theme)) return `dark-mode theme-${theme}`;
    return "";
}

export async function getLuminanceFromImage(base64: string): Promise<number> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";

        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

            canvas.width = img.width;
            canvas.height = img.height;

            ctx.drawImage(img, 0, 0, img.width, img.height);

            const imageData = ctx.getImageData(0, 0, img.width, img.height);
            const pixels = imageData.data; // [R, G, B, A, R, G, B, A, ...]

            let totalLuminance = 0;
            const pixelCount = pixels.length / 4;

            for (let i = 0; i < pixels.length; i += 4) {
                const r = pixels[i];
                const g = pixels[i + 1];
                const b = pixels[i + 2];

                const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b);
                totalLuminance += luminance;
            }

            const averageLuminance = totalLuminance / pixelCount;

            resolve(averageLuminance);
        };

        img.onerror = (error) => {
            reject(error);
        };

        img.src = base64;
    });
}